import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import * as _ from 'lodash';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { environment } from '@env/environment';
import jwt_decode from 'jwt-decode';

declare let google: any;

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  public static readonly SESSION_STORAGE_KEY = 'idToken';
  public static readonly SESSION_STORAGE_TOKEN = 'accessToken';

  public redirectUrl: string;
  private user: any = undefined;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private router: Router,
  ) {}

  public setUser(user: any): void {
    this.user = user;
  }

  public getCurrentUser(): any {
    return this.user;
  }

  public getAccessToken(): string {
    const accessToken = sessionStorage.getItem(AuthService.SESSION_STORAGE_TOKEN);
    if (!accessToken) {
      this.signOut();
      throw new Error('No access token');
    }
    return accessToken;
  }

  public getToken(): string {
    const token = sessionStorage.getItem(AuthService.SESSION_STORAGE_KEY);
    if (!token) {
      this.signOut();
      return null;
    }
    return token;
  }

  decodeToken(token: string): any {
    return jwt_decode(token);
  }

  handleOnTapeSignIn(credentials: string) {
    this.tokenHandler(credentials)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response: any) => {
        if (response?.errors) {
          this.navigate('login');
        }
        if (response?.email) {
          this.oauthSignIn(response.email);
          this.successSignInHandler(credentials, response);
        }
      });
  }

  oauthSignIn(googleId: string) {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: environment.googleClientId,
      scope: 'email',
      hint: googleId,
      prompt: '',
      callback: (tokenResponse) => {
        if (tokenResponse?.access_token) {
          sessionStorage.setItem(
            AuthService.SESSION_STORAGE_TOKEN,
            tokenResponse.access_token
          );
        }
      },
    });
    client.requestAccessToken();
  }

  public signOut(): void {
    sessionStorage.clear();
    this.setUser(undefined);
    this.navigate('login');
  }

  public isUserSignedIn(): boolean {
    return !!sessionStorage.getItem(AuthService.SESSION_STORAGE_KEY);
  }

  public navigate(path: string): void {
    this.ngZone.run(() => this.router.navigateByUrl(path)).catch(() => {});
  }

  private successSignInHandler(creds: string, user: any) {
    this.ngZone.run(() => {
      this.setUser(user);
      sessionStorage.setItem(AuthService.SESSION_STORAGE_KEY, creds);
      this.navigate(this.redirectUrl ?? '');
      this.redirectUrl = null;
    });
  }

  public tokenHandler(token: string): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}/fraud/verifyToken`,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
      }
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}

